import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Chip, IconButton, Alert, CircularProgress, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentsIcon from '@mui/icons-material/Payments';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSelector, useDispatch } from 'react-redux';
import { login } from '../store/authSlice';
import agentService from '../services/agentService';

const AgentPayouts = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [payingOut, setPayingOut] = useState(null);
  const [payoutNotes, setPayoutNotes] = useState('');

  // Payout confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, agent: null });

  // History dialog
  const [historyDialog, setHistoryDialog] = useState({ open: false, agent: null, records: [], loading: false });

  // All payout history
  const [allHistory, setAllHistory] = useState([]);
  const [allHistoryLoading, setAllHistoryLoading] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      dispatch(login(user));
      const data = await agentService.getPendingAgentEarnings();
      setAgents(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load agent earnings. Please try again.');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, user]);

  const loadAllHistory = useCallback(async () => {
    setAllHistoryLoading(true);
    try {
      const data = await agentService.getAllAgentPayoutHistory();
      setAllHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load payout history.');
    } finally {
      setAllHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const handlePayout = async () => {
    const agent = confirmDialog.agent;
    if (!agent) return;
    setPayingOut(agent._id);
    setConfirmDialog({ open: false, agent: null });
    setError(null);
    setSuccess(null);
    try {
      const result = await agentService.payoutAgent(agent._id, payoutNotes);
      setSuccess(`Payout of $${result.totalPayoutAmount?.toFixed(2) || '0.00'} to ${agent.name} completed successfully.`);
      setPayoutNotes('');
      await loadAgents();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || 'Payout failed. Please try again.');
    } finally {
      setPayingOut(null);
    }
  };

  const handleViewHistory = async (agent) => {
    setHistoryDialog({ open: true, agent, records: [], loading: true });
    try {
      const records = await agentService.getAgentPayoutHistory(agent._id);
      setHistoryDialog((prev) => ({ ...prev, records: Array.isArray(records) ? records : [], loading: false }));
    } catch (err) {
      setHistoryDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amt) => {
    const n = Number(amt || 0);
    return `$${n.toFixed(2)}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress sx={{ color: '#2bb6a9' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Tabs value={activeTab} onChange={(_, v) => { setActiveTab(v); if (v === 1 && allHistory.length === 0) loadAllHistory(); }}
        sx={{ mb: 2, '& .MuiTab-root': { color: '#8b949e' }, '& .Mui-selected': { color: '#2bb6a9 !important' }, '& .MuiTabs-indicator': { backgroundColor: '#2bb6a9' } }}>
        <Tab label="Pending Earnings" />
        <Tab label="Payout History" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ─── Tab 0: Pending Earnings ─── */}
      {activeTab === 0 && (
        <>
          <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: '#8b949e' }}>
              {agents.length} agent{agents.length !== 1 ? 's' : ''} · {agents.filter((a) => a.pendingEarnings > 0).length} with pending earnings
            </Typography>
            <Box sx={{ flex: 1 }} />
            <IconButton size="small" onClick={loadAgents} sx={{ color: '#8b949e' }}><RefreshIcon /></IconButton>
          </Paper>

          <Paper elevation={0} sx={{ border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Agent</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Athletes</TableCell>
                    <TableCell>Pending Earnings</TableCell>
                    <TableCell>Pending Entries</TableCell>
                    <TableCell>Oldest Pending</TableCell>
                    <TableCell>Total Paid Out</TableCell>
                    <TableCell>Stripe</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agents.length === 0 ? (
                    <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: '#8b949e' }}>No agents found.</TableCell></TableRow>
                  ) : agents.map((agent) => (
                    <TableRow key={agent._id}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{agent.name}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {agent.email}
                        <IconButton size="small" sx={{ color: '#8b949e', p: 0.3, ml: 0.5 }} onClick={() => copyToClipboard(agent.email)}>
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </TableCell>
                      <TableCell>{agent.totalAthletes}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: agent.pendingEarnings > 0 ? '#81c784' : '#8b949e' }}>
                        {formatCurrency(agent.pendingEarnings)}
                      </TableCell>
                      <TableCell>{agent.pendingEntryCount}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(agent.oldestPendingDate)}</TableCell>
                      <TableCell>{formatCurrency(agent.totalPaidOut)}</TableCell>
                      <TableCell>
                        <Chip label={agent.stripeReady ? 'Ready' : 'Not Set Up'} size="small"
                          sx={{ bgcolor: agent.stripeReady ? 'rgba(76,175,80,0.15)' : 'rgba(244,67,54,0.15)',
                            color: agent.stripeReady ? '#81c784' : '#f44336', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={agent.isActive ? 'Active' : 'Inactive'} size="small"
                          sx={{ bgcolor: agent.isActive ? 'rgba(76,175,80,0.15)' : 'rgba(139,148,158,0.15)',
                            color: agent.isActive ? '#81c784' : '#8b949e', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button size="small" variant="contained"
                            disabled={agent.pendingEarnings <= 0 || !agent.stripeReady || payingOut === agent._id}
                            startIcon={payingOut === agent._id ? <CircularProgress size={14} /> : <PaymentsIcon />}
                            onClick={() => { setPayoutNotes(''); setConfirmDialog({ open: true, agent }); }}
                            sx={{ fontSize: '0.7rem', py: 0.3, textTransform: 'none' }}>
                            {payingOut === agent._id ? 'Processing...' : 'Payout'}
                          </Button>
                          <IconButton size="small" onClick={() => handleViewHistory(agent)} sx={{ color: '#8b949e' }}>
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* ─── Tab 1: All Payout History ─── */}
      {activeTab === 1 && (
        <>
          <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: '#8b949e' }}>{allHistory.length} payout record{allHistory.length !== 1 ? 's' : ''}</Typography>
            <Box sx={{ flex: 1 }} />
            <IconButton size="small" onClick={loadAllHistory} sx={{ color: '#8b949e' }}><RefreshIcon /></IconButton>
          </Paper>

          {allHistoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#2bb6a9' }} /></Box>
          ) : (
            <Paper elevation={0} sx={{ border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Agent</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Stripe Transfer</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Triggered By</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allHistory.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: '#8b949e' }}>No payout records yet.</TableCell></TableRow>
                    ) : allHistory.map((record) => (
                      <TableRow key={record._id}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{record.trainerName}</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#81c784' }}>{formatCurrency(record.totalPayoutAmount)}</TableCell>
                        <TableCell>
                          <Chip label={record.status} size="small"
                            sx={{ fontWeight: 600,
                              bgcolor: record.status === 'completed' ? 'rgba(76,175,80,0.15)' : record.status === 'failed' ? 'rgba(244,67,54,0.15)' : 'rgba(255,152,0,0.15)',
                              color: record.status === 'completed' ? '#81c784' : record.status === 'failed' ? '#f44336' : '#ff9800',
                            }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                          {record.stripeTransferId || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {formatDate(record.periodStart)} – {formatDate(record.periodEnd)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{record.triggeredByName || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {record.notes || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(record.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* ─── Confirm Payout Dialog ─── */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, agent: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Agent Payout</DialogTitle>
        <DialogContent>
          {confirmDialog.agent && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Pay <strong>{confirmDialog.agent.name}</strong> their pending commission of{' '}
                <strong style={{ color: '#81c784' }}>{formatCurrency(confirmDialog.agent.pendingEarnings)}</strong>?
              </Typography>
              <Typography variant="body2" sx={{ color: '#8b949e', mb: 2 }}>
                This will transfer funds to their Stripe account and mark {confirmDialog.agent.pendingEntryCount} ledger entries as paid.
              </Typography>
              <TextField fullWidth size="small" label="Notes (optional)" value={payoutNotes}
                onChange={(e) => setPayoutNotes(e.target.value)} multiline rows={2} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, agent: null })}>Cancel</Button>
          <Button variant="contained" onClick={handlePayout} startIcon={<PaymentsIcon />}>Confirm Payout</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Agent Payout History Dialog ─── */}
      <Dialog open={historyDialog.open} onClose={() => setHistoryDialog({ open: false, agent: null, records: [], loading: false })} maxWidth="md" fullWidth>
        <DialogTitle>Payout History — {historyDialog.agent?.name}</DialogTitle>
        <DialogContent>
          {historyDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#2bb6a9' }} /></Box>
          ) : historyDialog.records.length === 0 ? (
            <Typography sx={{ py: 4, textAlign: 'center', color: '#8b949e' }}>No payout history for this agent.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Stripe Transfer</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyDialog.records.map((r) => (
                    <TableRow key={r._id}>
                      <TableCell sx={{ fontWeight: 700, color: '#81c784' }}>{formatCurrency(r.totalPayoutAmount)}</TableCell>
                      <TableCell>
                        <Chip label={r.status} size="small"
                          sx={{ fontWeight: 600,
                            bgcolor: r.status === 'completed' ? 'rgba(76,175,80,0.15)' : r.status === 'failed' ? 'rgba(244,67,54,0.15)' : 'rgba(255,152,0,0.15)',
                            color: r.status === 'completed' ? '#81c784' : r.status === 'failed' ? '#f44336' : '#ff9800',
                          }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{r.stripeTransferId || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(r.periodStart)} – {formatDate(r.periodEnd)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{r.notes || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(r.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog({ open: false, agent: null, records: [], loading: false })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentPayouts;
